/*
  Homepage community reviews (sections/community-reviews.liquid).

  Progressive enhancement over plain markup. Without this file each TikTok
  card links to its video on TikTok, every review reads in full to screen
  readers, and the product links work.

  - Rows: arrows scroll a row a card at a time and are shown only while it
    overflows (assets/media-rows.js).
  - TikTok cards: a click swaps the poster for TikTok's embed player
    (tiktok.com/player/v1), so no TikTok code loads until someone asks for a
    video. One plays at a time on the whole page (media-rows.js), and a
    player is thrown away again once its card leaves the screen, which is
    what stops the sound.
  - Photo reviews: the photo opens the full picture and the whole review in
    a native <dialog>, filled from the card's <template>. Previous and next
    step through the reviews and wrap around. A "Read more" under the text
    opens the same dialog; it is shown only while the text is cut off by its
    line clamp, which also makes a review without a photo readable in full.
*/
(() => {
  if (customElements.get('community-reviews')) return;
  const rows = window.chellaMediaRows;
  if (!rows) return;

  class CommunityReviews extends HTMLElement {
    /** @type {AbortController | null} */
    #abort = null;
    /** @type {IntersectionObserver | null} */
    #playerObserver = null;
    /** @type {ResizeObserver | null} re-checks which review texts are cut off */
    #clampObserver = null;
    /** @type {HTMLElement | null} the media box whose player is showing */
    #playing = null;
    #dialog = null;
    #detailTarget = null;
    /** @type {HTMLElement[]} review cards that have a photo to open */
    #reviews = [];
    #index = 0;
    #opener = null;
    #bodyPadding = '';
    /** Passed to claimPlayer, so another section's video can stop this one. */
    #stop = () => this.#stopVideo();

    connectedCallback() {
      this.#abort = new AbortController();
      const { signal } = this.#abort;

      for (const row of this.querySelectorAll('[data-community-row]')) this.#setUpRow(row, signal);

      this.#setUpVideos(signal);
      this.#setUpDialog(signal);

      // Theme editor: selecting a block brings its card into its row's view.
      this.addEventListener(
        'shopify:block:select',
        (event) => {
          if (!(event.target instanceof Element)) return;
          event.target.scrollIntoView({ block: 'nearest', inline: 'start' });
        },
        { signal }
      );
    }

    disconnectedCallback() {
      this.#abort?.abort();
      this.#playerObserver?.disconnect();
      this.#clampObserver?.disconnect();
      this.#stopVideo();

      // The theme editor swaps the section out from under an open dialog.
      if (this.#dialog?.open) this.#dialog.close();
      this.#unlockScroll();
    }

    /* ---------------------------------------------------------------- Rows */

    /**
     * @param {Element} row
     * @param {AbortSignal} signal
     */
    #setUpRow(row, signal) {
      const track = row.querySelector('[data-community-track]');
      if (!(track instanceof HTMLElement)) return;
      rows.attachRow({
        track,
        controls: row.querySelector('[data-community-controls]'),
        arrows: Array.from(row.querySelectorAll('[data-community-scroll]')),
        direction: (arrow) => Number(arrow.dataset.communityScroll),
        signal,
      });
    }

    /* -------------------------------------------------------------- Videos */

    /** @param {AbortSignal} signal */
    #setUpVideos(signal) {
      // Off screen means no longer wanted: drop the player and its sound.
      this.#playerObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting && entry.target === this.#playing) this.#stopVideo();
        }
      });

      this.addEventListener(
        'click',
        (event) => {
          const poster = event.target instanceof Element ? event.target.closest('[data-community-play]') : null;
          if (!(poster instanceof HTMLElement) || !this.contains(poster)) return;
          // Let modified clicks open TikTok in a new tab as usual.
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          this.#playVideo(poster);
        },
        { signal }
      );
    }

    /** @param {HTMLElement} poster */
    #playVideo(poster) {
      const media = poster.closest('[data-community-media]');
      if (!(media instanceof HTMLElement) || !poster.dataset.embed) return;
      this.#stopVideo();

      const frame = rows.createPlayer(poster, 'community-video__frame', 'data-community-frame');
      poster.hidden = true;
      media.append(frame);
      frame.focus({ preventScroll: true });

      this.#playing = media;
      this.#playerObserver?.observe(media);
      rows.claimPlayer(this.#stop);
    }

    #stopVideo() {
      const media = this.#playing;
      if (!media) return;
      this.#playing = null;
      this.#playerObserver?.unobserve(media);
      rows.releasePlayer(this.#stop);

      const frameHadFocus = media.contains(document.activeElement);
      media.querySelector('[data-community-frame]')?.remove();
      const poster = media.querySelector('[data-community-play]');
      if (poster instanceof HTMLElement) {
        poster.hidden = false;
        if (frameHadFocus) poster.focus({ preventScroll: true });
      }
    }

    /* -------------------------------------------------------------- Dialog */

    /** @param {AbortSignal} signal */
    #setUpDialog(signal) {
      this.#dialog = this.querySelector('[data-community-dialog]');
      this.#detailTarget = this.querySelector('[data-community-detail-target]');
      if (!this.#dialog || !this.#detailTarget) return;

      this.#reviews = Array.from(this.querySelectorAll('[data-community-card]')).filter((card) =>
        card.querySelector('[data-community-detail]')
      );
      const single = this.#reviews.length < 2;
      for (const button of this.#dialog.querySelectorAll('[data-community-step]')) {
        button.hidden = single;
      }

      this.#setUpReadMore();

      this.addEventListener(
        'click',
        (event) => {
          const opener = event.target instanceof Element ? event.target.closest('[data-community-open]') : null;
          if (!opener || !this.contains(opener)) return;
          const card = opener.closest('[data-community-card]');
          const position = this.#reviews.indexOf(/** @type {HTMLElement} */ (card));
          if (position < 0) return;
          this.#opener = opener;
          this.#open(position);
        },
        { signal }
      );

      this.#dialog.addEventListener(
        'click',
        (event) => {
          const target = event.target instanceof Element ? event.target : null;
          if (!target) return;
          // A click on the backdrop lands on the <dialog> itself.
          if (target === this.#dialog || target.closest('[data-community-close]')) {
            this.#dialog.close();
            return;
          }
          const step = target.closest('[data-community-step]');
          if (step instanceof HTMLElement) this.#show(this.#index + Number(step.dataset.communityStep));
        },
        { signal }
      );

      this.#dialog.addEventListener(
        'keydown',
        (event) => {
          if (this.#reviews.length < 2) return;
          if (event.key === 'ArrowRight') this.#show(this.#index + 1);
          if (event.key === 'ArrowLeft') this.#show(this.#index - 1);
        },
        { signal }
      );

      this.#dialog.addEventListener('close', () => this.#onClose(), { signal });
    }

    /* "Read more" shows only while its review text overflows the clamp. The
       clamp depends on the card width and the web font, so it is checked
       again on resize and once the fonts have loaded. */
    #setUpReadMore() {
      const texts = Array.from(this.querySelectorAll('[data-community-text]'));
      if (!texts.length) return;

      const update = () => {
        for (const text of texts) {
          const more = text.parentElement?.querySelector('[data-community-more]');
          if (!(more instanceof HTMLElement)) continue;
          const cut = text.scrollHeight > text.clientHeight + 1;
          // A focused button that hides drops focus to the page; keep it.
          if (!cut && document.activeElement === more) continue;
          more.hidden = !cut;
        }
      };

      this.#clampObserver = new ResizeObserver(update);
      for (const text of texts) this.#clampObserver.observe(text);
      document.fonts?.ready.then(update);
      update();
    }

    /** @param {number} position */
    #open(position) {
      if (!this.#dialog) return;
      // Any video on the page, this section's or the trending one, would
      // keep playing out of sight behind the modal.
      rows.stopActive();
      this.#show(position);
      if (!this.#dialog.open) {
        this.#lockScroll();
        this.#dialog.showModal();
      }
      this.#dialog.querySelector('[data-community-close]')?.focus();
    }

    /** @param {number} position - wraps around at either end */
    #show(position) {
      const total = this.#reviews.length;
      if (!total || !this.#detailTarget) return;
      this.#index = ((position % total) + total) % total;

      const template = this.#reviews[this.#index].querySelector('[data-community-detail]');
      if (!(template instanceof HTMLTemplateElement)) return;
      // Arrow keys can step while focus is on a link inside the old review;
      // keep focus in the dialog rather than losing it with the old content.
      const hadFocus = this.#detailTarget.contains(document.activeElement);
      this.#detailTarget.replaceChildren(template.content.cloneNode(true));
      if (hadFocus) this.#dialog?.querySelector('[data-community-close]')?.focus();
      if (this.#dialog) this.#dialog.scrollTop = 0;
    }

    #onClose() {
      this.#detailTarget?.replaceChildren();
      this.#unlockScroll();

      // Return focus to the card now showing, which may not be the one opened:
      // the button used to open it if that is this card's, otherwise the
      // card's first visible opener (its photo, or "Read more" without one).
      const card = this.#reviews[this.#index];
      const openers = card ? Array.from(card.querySelectorAll('[data-community-open]')).filter((el) => !el.hidden) : [];
      const target = (this.#opener && openers.includes(this.#opener) ? this.#opener : openers[0]) || this.#opener;
      if (target instanceof HTMLElement && target.isConnected) {
        target.focus({ preventScroll: true });
        card?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
      this.#opener = null;
    }

    #lockScroll() {
      const root = document.documentElement;
      if (root.classList.contains('has-community-modal')) return;

      const gap = window.innerWidth - root.clientWidth;
      this.#bodyPadding = document.body.style.paddingRight;
      if (gap > 0) document.body.style.paddingRight = `${gap}px`;
      root.classList.add('has-community-modal');
    }

    #unlockScroll() {
      const root = document.documentElement;
      if (!root.classList.contains('has-community-modal')) return;

      root.classList.remove('has-community-modal');
      document.body.style.paddingRight = this.#bodyPadding;
    }
  }

  customElements.define('community-reviews', CommunityReviews);
})();
